import { useState, useEffect, useCallback, useMemo } from 'react';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import Modal from '@components/common/Modal';
import Toast from '@components/common/Toast';
import {
  REPORT_TABS, REPORT_COLUMNS,
  searchReports, getReportDownloadUrl, downloadReportsAsZip, shareReportByEmail,
} from '@services/api/reportsService';

/**
 * ReportsPage — My Reports
 * Route: /reports
 *
 * Features:
 * - Search bar across site names, customer names, failure reasons
 * - Left sidebar with collapsible advanced filters
 *   (Sales area, Sales entity, Customer name, Site name, Date range)
 * - 4 tabs with record counts: Rock tool cards | Customer site visits | Site audits | Rig audits
 * - Data table with columns per tab type
 * - Row actions: copy | download | share (email only) | view | favourite
 * - Multi-select checkbox + download selected as ZIP
 * - Share restricted to email (Outlook/Gmail) — no social media sharing
 */

/* ─── Icon components ─── */
function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
function ShareIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}
function ViewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function StarIcon({ filled }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24"
         fill={filled ? '#A08C64' : 'none'} stroke={filled ? '#A08C64' : 'currentColor'} strokeWidth="1.5">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

/* ─── Sidebar filter component ─── */
function FilterSidebar({ filters, onChange, onClear }) {
  return (
    <aside className="w-64 flex-shrink-0 border-r border-n2 pr-4 space-y-5">
      <div className="flex items-center justify-between">
        <button
          onClick={onClear}
          className="flex items-center gap-1.5 text-xs text-n5 hover:text-carbon-black transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
          Clear all filters
        </button>
      </div>
      <p className="text-[10px] text-n4 italic">
        All filters use partial matching and work with general search above
      </p>

      {/* Location & customer */}
      <FilterSection title="Location &amp; customer">
        <FilterSelect label="Sales area" value={filters.salesArea} onChange={v => onChange('salesArea', v)}
                       placeholder="Select sales area" />
        <FilterSelect label="Sales entity" value={filters.salesEntity} onChange={v => onChange('salesEntity', v)}
                       placeholder="Select sales entity" />
        <FilterSelect label="Customer name" value={filters.customerName} onChange={v => onChange('customerName', v)}
                       placeholder="Select customer" />
        <FilterSelect label="Site name" value={filters.siteName} onChange={v => onChange('siteName', v)}
                       placeholder="Select site" />
      </FilterSection>

      {/* Time span */}
      <FilterSection title="Time span">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] text-n5 mb-1">From date</label>
            <input type="date" value={filters.dateFrom || ''}
                   onChange={e => onChange('dateFrom', e.target.value)}
                   className="w-full border border-n3 rounded px-2 py-1.5 text-xs" />
          </div>
          <div>
            <label className="block text-[10px] text-n5 mb-1">To date</label>
            <input type="date" value={filters.dateTo || ''}
                   onChange={e => onChange('dateTo', e.target.value)}
                   className="w-full border border-n3 rounded px-2 py-1.5 text-xs" />
          </div>
        </div>
      </FilterSection>

      {/* Other filters placeholder */}
      <FilterSection title="Other filters">
        <p className="text-[10px] text-n4 italic">
          Additional filter options
        </p>
      </FilterSection>
    </aside>
  );
}

function FilterSection({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs font-semibold text-carbon-black mb-2 w-full"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
             className={`transition-transform ${open ? 'rotate-90' : ''}`}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
        {title}
      </button>
      {open && <div className="space-y-3 pl-4">{children}</div>}
    </div>
  );
}

function FilterSelect({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] text-n5 mb-1">{label}</label>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-n3 rounded px-2 py-1.5 text-xs text-n5
                   focus:ring-1 focus:ring-electric-blue"
      >
        <option value="">{placeholder}</option>
      </select>
    </div>
  );
}

/* ─── Share modal (email only) ─── */
function ShareModal({ isOpen, onClose, reportId, onShare }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email.trim()) return;
    setSending(true);
    await onShare(reportId, { recipientEmail: email, message });
    setSending(false);
    setEmail('');
    setMessage('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share report via email" size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose}
                  className="px-4 py-2 border border-n3 rounded text-sm text-carbon-black hover:bg-n1">
            Cancel
          </button>
          <button onClick={handleSend} disabled={!email.trim() || sending}
                  className="px-4 py-2 bg-electric-blue text-white rounded text-sm font-medium
                             hover:bg-blue-dark disabled:opacity-50">
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-n5">
          Share this report with a colleague via email (Outlook or Gmail).
        </p>
        <div>
          <label className="block text-sm font-medium text-n6 mb-1">Recipient email *</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                 placeholder="colleague@company.com"
                 className="w-full border border-n3 rounded px-3 py-2 text-sm
                            focus:ring-2 focus:ring-electric-blue focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-n6 mb-1">Message (optional)</label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
                    placeholder="Add a note..."
                    className="w-full border border-n3 rounded px-3 py-2 text-sm resize-y
                               focus:ring-2 focus:ring-electric-blue focus:border-transparent" />
        </div>
      </div>
    </Modal>
  );
}

/* ─── Main page ─── */
export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('rock-tool-cards');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const [shareReportId, setShareReportId] = useState(null);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const columns = REPORT_COLUMNS[activeTab] || [];

  // Fetch data on tab/filter change
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setSelectedIds(new Set());
    searchReports({ tabKey: activeTab, query, filters }).then(data => {
      if (mounted) {
        setRecords(data.records);
        setTotalCount(data.totalCount);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, [activeTab, query, filters]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setQuery('');
  }, []);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === records.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(records.map(r => r.id)));
    }
  };

  const toggleFavourite = (id) => {
    setFavouriteIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDownload = async (id) => {
    try {
      await getReportDownloadUrl(id);
      setToast({ type: 'success', message: 'Downloading report...' });
    } catch {
      setToast({ type: 'error', message: 'Download failed.' });
    }
  };

  const handleDownloadZip = async () => {
    if (selectedIds.size === 0) return;
    try {
      await downloadReportsAsZip([...selectedIds]);
      setToast({ type: 'success', message: `Downloading ${selectedIds.size} reports as ZIP...` });
    } catch {
      setToast({ type: 'error', message: 'ZIP download failed.' });
    }
  };

  const handleCopy = (record) => {
    const text = columns.map(c => `${c.label}: ${record[c.key] || ''}`).join('\n');
    navigator.clipboard?.writeText(text);
    setToast({ type: 'success', message: 'Copied to clipboard.' });
  };

  const handleView = (record) => {
    // TODO: Open report detail view / PDF viewer
    console.log('[ReportsPage] View report:', record.id);
    setToast({ type: 'success', message: 'Opening report...' });
  };

  const handleShare = async (reportId, shareData) => {
    try {
      await shareReportByEmail(reportId, shareData);
      setToast({ type: 'success', message: 'Report shared via email.' });
    } catch {
      setToast({ type: 'error', message: 'Failed to share report.' });
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-8">
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'My reports' }]} />
      <h1 className="font-sandvik-headline font-bold text-2xl text-carbon-black mb-6">
        My reports
      </h1>

      {/* Search bar */}
      <div className="flex gap-3 mb-6">
        <div className="flex-1 relative">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#575757" strokeWidth="2"
               className="absolute left-3 top-1/2 -translate-y-1/2">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search across site names, customer names, failure reasons"
            className="w-full border border-n3 rounded pl-10 pr-4 py-3 text-sm
                       focus:ring-2 focus:ring-electric-blue focus:border-transparent"
          />
        </div>
        <button
          onClick={() => {/* search is reactive via useEffect */}}
          className="px-6 py-3 bg-electric-blue text-white rounded text-sm font-medium
                     hover:bg-blue-dark transition-colors flex items-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          Search
        </button>
        <button className="p-3 border border-n3 rounded text-n5 hover:text-carbon-black hover:bg-n1"
                title="Search tips">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </button>
      </div>

      <div className="flex gap-6">
        {/* Sidebar filters */}
        {sidebarOpen && (
          <FilterSidebar filters={filters} onChange={handleFilterChange} onClear={handleClearFilters} />
        )}

        {/* Main content area */}
        <div className="flex-1 min-w-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="mb-3 p-1.5 text-n5 hover:text-carbon-black hover:bg-n1 rounded transition-colors"
            title={sidebarOpen ? 'Hide filters' : 'Show filters'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {sidebarOpen ? (
                <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
              ) : (
                <><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                  <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                  <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
                  <line x1="17" y1="16" x2="23" y2="16" /></>
              )}
            </svg>
          </button>

          {/* Tabs with counts */}
          <div className="border-b border-n2 mb-4">
            <nav className="flex gap-4" role="tablist">
              {REPORT_TABS.map(tab => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
                    relative pb-3 text-sm font-medium transition-colors flex items-center gap-1.5
                    ${activeTab === tab.key ? 'text-carbon-black' : 'text-n5 hover:text-carbon-black'}
                  `}
                >
                  {tab.label}
                  <span className={`
                    px-1.5 py-0.5 rounded text-[10px]
                    ${activeTab === tab.key ? 'bg-carbon-black text-white' : 'bg-n2 text-n5'}
                  `}>
                    {tab.count.toLocaleString()}
                  </span>
                  {activeTab === tab.key && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-blue rounded-t" />
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Multi-select actions bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 mb-3 p-2 bg-blue-light/20 rounded">
              <span className="text-xs text-electric-blue">
                {selectedIds.size} selected
              </span>
              <button
                onClick={handleDownloadZip}
                className="text-xs text-electric-blue hover:underline flex items-center gap-1"
              >
                <DownloadIcon /> Download as ZIP
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-n5 hover:text-carbon-black ml-auto"
              >
                Clear selection
              </button>
            </div>
          )}

          {/* Data table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-10 text-sm text-n5">
              No records found. Try adjusting your search or filters.
            </div>
          ) : (
            <div className="overflow-x-auto border border-n2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-carbon-black text-white">
                    <th className="px-3 py-2.5 text-left w-8">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === records.length && records.length > 0}
                        onChange={toggleSelectAll}
                        className="w-3.5 h-3.5 rounded"
                      />
                    </th>
                    {columns.map(col => (
                      <th key={col.key} className="px-3 py-2.5 text-left text-xs font-semibold whitespace-nowrap">
                        {col.label}
                      </th>
                    ))}
                    <th className="px-3 py-2.5 text-left text-xs font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, idx) => (
                    <tr
                      key={record.id}
                      className={`
                        border-b border-n2 hover:bg-blue-light/10 transition-colors
                        ${selectedIds.has(record.id) ? 'bg-gold-light/30' : idx % 2 === 1 ? 'bg-n1/30' : ''}
                      `}
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(record.id)}
                          onChange={() => toggleSelect(record.id)}
                          className="w-3.5 h-3.5 rounded"
                        />
                      </td>
                      {columns.map(col => (
                        <td key={col.key} className="px-3 py-2.5 text-xs text-carbon-black whitespace-nowrap max-w-[160px] truncate">
                          {record[col.key] || '—'}
                        </td>
                      ))}
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => handleCopy(record)} title="Copy"
                                  className="p-1 text-n5 hover:text-electric-blue rounded transition-colors">
                            <CopyIcon />
                          </button>
                          <button onClick={() => handleDownload(record.id)} title="Download"
                                  className="p-1 text-n5 hover:text-electric-blue rounded transition-colors">
                            <DownloadIcon />
                          </button>
                          <button onClick={() => setShareReportId(record.id)} title="Share via email"
                                  className="p-1 text-n5 hover:text-electric-blue rounded transition-colors">
                            <ShareIcon />
                          </button>
                          <button onClick={() => handleView(record)} title="View"
                                  className="p-1 text-n5 hover:text-electric-blue rounded transition-colors">
                            <ViewIcon />
                          </button>
                          <button onClick={() => toggleFavourite(record.id)} title="Favourite"
                                  className="p-1 text-n5 hover:text-gold rounded transition-colors">
                            <StarIcon filled={favouriteIds.has(record.id)} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && records.length > 0 && (
            <p className="text-xs text-n5 mt-3">
              Showing {records.length} of {totalCount.toLocaleString()} records
            </p>
          )}
        </div>
      </div>

      {/* Share via email modal */}
      <ShareModal
        isOpen={!!shareReportId}
        onClose={() => setShareReportId(null)}
        reportId={shareReportId}
        onShare={handleShare}
      />

      {/* Toast */}
      {toast && (
        <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
